/**
 * Friend Profile API client.
 * 
 * Wires PWA Friend happy path to backend profile API.
 * 
 * Security posture:
 * - Identity-minimized payloads (no legal name, phone, address, email, etc.)
 * - Account ID is server-derived from authenticated session
 * - No raw fingerprint or anti-fraud internals exposed
 * - Graceful error handling for demo safety
 * - Endpoint selection by registration kind (server-side enforcement)
 * 
 * API contract: See docs/api/profile-api.md
 * Gender/goal/age per REQ-125, REQ-126, REQ-127.
 */

import type { RegistrationKind } from '@/store/authStore';

const API_BASE = '/api/v1';

// =============================================================================
// Types
// =============================================================================

/**
 * Gender enum per REQ-126.
 * User chooses gender first, then goal.
 */
export type Gender = 'male' | 'female';

/**
 * Friend goal enum per REQ-127.
 * Available goals depend on gender selection.
 */
export type FriendGoal = 'equal_seeker' | 'companion_seeker' | 'no_work';

/**
 * Sponsor goal enum per REQ-127.
 */
export type SponsorGoal = 'wealthy_seeker';

/**
 * Friend profile data shape.
 * All fields are identity-minimized per safety doctrine.
 * Includes activation fields per REQ-125.
 */
export interface FriendProfile {
  /** Profile UUID (internal identifier, not account-level identity) */
  id: string;
  /** Always "friend" for these endpoints */
  profile_type: 'friend';
  /** Profile lifecycle status: inactive/active/blocked/deleted per REQ-081 */
  profile_status: 'inactive' | 'active' | 'blocked' | 'deleted';
  /** User's gender selection per REQ-126 (nil until user selects) */
  gender?: Gender;
  /** User's goal per REQ-127 (nil until user selects after gender) */
  goal?: FriendGoal;
  /** User's age (0 means not set) */
  age?: number;
  /** Pseudonymous display name (not legal name) */
  display_name?: string;
  /** Short tagline about values/interests */
  headline?: string;
  /** Freeform description (max 1000 chars) */
  bio?: string;
  /** Geographic hint (city/region only, no full address) */
  city_or_region?: string;
  /** ISO 8601 timestamp */
  updated_at: string;
  /** Missing activation field codes for frontend UX per REQ-125 */
  missing_activation_fields?: string[];
  /**
   * Optional work sphere (PC-043 / B-2.A contract).
   *
   * Wire shape (per frozen B-2.A contract §2): the GET response may
   *   - omit the property (forward-compat with partial rollouts)
   *   - send `null` (canonical "not set")
   *   - send a non-empty string
   *
   * The FE canonicalises all three to `undefined` form-state.
   * `null` is intentionally distinct from `undefined` here so the
   * transport DTO does not pretend to know which cases the server
   * actually emits; OpenAPI 3.0 distinguishes nullable from required.
   */
  work_sphere?: string | null;
  /**
   * Optional education sphere (PC-044 / B-2.A contract). Same wire
   * semantics as `work_sphere`.
   */
  education_sphere?: string | null;
  /**
   * Optional education type (PC-045 / B-2.A contract). Same wire
   * semantics as `work_sphere`.
   */
  education_type?: string | null;
  /**
   * B-2.B: optional durable photo list (PHOTO_MAX_COUNT = 6).
   *
   * The wire shape is `photos: ProfilePhoto[]` from the B-2.B
   * backend contract. The FE normalizes the canonical list via
   * `normalizeProfilePhotosField` (single source of truth in
   * `frontend/src/api/profilePhotos.ts`). The FE MUST NOT
   * reconstruct role-prefixed content URLs from photo IDs; URLs
   * arrive verbatim from the server.
   *
   * Optional for forward compatibility with pre-B-2.B rollouts;
   * absence / `null` / empty array are all canonical "no photos".
   */
  photos?: readonly import('./profilePhotos').ProfilePhoto[];
}

/**
 * Sponsor profile data shape.
 * All fields are identity-minimized per safety doctrine.
 * Includes activation fields per REQ-125.
 */
export interface SponsorProfile {
  /** Profile UUID (internal identifier, not account-level identity) */
  id: string;
  /** Always "sponsor" for these endpoints */
  profile_type: 'sponsor';
  /** Profile lifecycle status: inactive/active/blocked/deleted per REQ-081 */
  profile_status: 'inactive' | 'active' | 'blocked' | 'deleted';
  /** User's gender selection per REQ-126 (nil until user selects) */
  gender?: Gender;
  /** User's goal per REQ-127 (nil until user selects after gender) */
  goal?: SponsorGoal;
  /** User's age (0 means not set) */
  age?: number;
  /** Pseudonymous display name (not legal name) */
  display_name?: string;
  /** Short tagline about interests */
  headline?: string;
  /** Freeform description (max 1000 chars) */
  bio?: string;
  /** Geographic hint (city/region only, no full address) */
  city_or_region?: string;
  /** ISO 8601 timestamp */
  updated_at: string;
  /** Missing activation field codes for frontend UX per REQ-125 */
  missing_activation_fields?: string[];
  /**
   * Optional work sphere (PC-043 / B-2.A contract). Same wire
   * semantics as `FriendProfile.work_sphere` (omitted | null |
   * non-empty string).
   */
  work_sphere?: string | null;
  /**
   * Optional education sphere (PC-044 / B-2.A contract).
   */
  education_sphere?: string | null;
  /**
   * Optional education type (PC-045 / B-2.A contract).
   */
  education_type?: string | null;
  /**
   * B-2.B: optional durable photo list (PHOTO_MAX_COUNT = 6).
   * Same wire / normalization contract as `FriendProfile.photos`.
   */
  photos?: readonly import('./profilePhotos').ProfilePhoto[];
}

/**
 * Fingerprint input for fraud-resistant audit context.
 * Reuses FingerprintSignals shape from auth API.
 */
export interface ProfileFingerprintInput {
  user_agent: string;
  accept_language: string;
  screen: string;
  timezone: string;
  platform: string;
  hardware_concurrency: number;
  canvas_hash: string;
  webgl_vendor: string;
  webgl_renderer: string;
  audio_hash: string;
  fonts_hash: string;
  touch_support: boolean;
  color_depth: number;
  device_memory: number;
}

/**
 * Request payload for creating/updating friend profile.
 * Fingerprint is optional - collection failure is non-blocking (fail-open).
 * Covers: BR-FRAUD-001, REQ-FRAUD-FINGERPRINT-CAPTURE-001
 * Gender/goal/age per REQ-125, REQ-126, REQ-127.
 */
export interface UpdateFriendProfileRequest {
  profile_type: 'friend';
  /** User's gender selection per REQ-126 (choose gender first) */
  gender?: Gender;
  /** User's goal per REQ-127 (available after gender is set) */
  goal?: FriendGoal;
  /** User's age (must be 18-120 per REQ-125) */
  age?: number;
  display_name?: string;
  headline?: string;
  bio?: string;
  city_or_region?: string;
  /**
   * Optional work sphere (B-2.A contract; see
   * docs/epics/profile-questionnaire-detail-slice-b-2/backend-dependency.md).
   * Length <= 64 chars. Omit to clear; sending null/empty canonicalises
   * to "not set" server-side.
   */
  work_sphere?: string;
  /**
   * Optional education sphere (B-2.A contract). Length <= 64 chars.
   */
  education_sphere?: string;
  /**
   * Optional education type (B-2.A contract). Length <= 64 chars.
   */
  education_type?: string;
  /** Optional fingerprint signals for fraud-resistant audit context */
  fingerprint?: ProfileFingerprintInput;
}

/**
 * Request payload for creating/updating sponsor profile.
 * Fingerprint is optional - collection failure is non-blocking (fail-open).
 * Covers: BR-FRAUD-001, REQ-FRAUD-FINGERPRINT-CAPTURE-001
 * Gender/goal/age per REQ-125, REQ-126, REQ-127.
 */
export interface UpdateSponsorProfileRequest {
  profile_type: 'sponsor';
  /** User's gender selection per REQ-126 (choose gender first) */
  gender?: Gender;
  /** User's goal per REQ-127 (available after gender is set) */
  goal?: SponsorGoal;
  /** User's age (must be 18-120 per REQ-125) */
  age?: number;
  display_name?: string;
  headline?: string;
  bio?: string;
  city_or_region?: string;
  /**
   * Optional work sphere (B-2.A contract). Length <= 64 chars.
   */
  work_sphere?: string;
  /**
   * Optional education sphere (B-2.A contract). Length <= 64 chars.
   */
  education_sphere?: string;
  /**
   * Optional education type (B-2.A contract). Length <= 64 chars.
   */
  education_type?: string;
  /** Optional fingerprint signals for fraud-resistant audit context */
  fingerprint?: ProfileFingerprintInput;
}

/**
 * Paginated list response for discovery.
 * Note: total field intentionally omitted - backend does not compute it.
 */
export interface ProfileListResponse {
  profiles: FriendProfile[];
  /** Current page limit */
  limit: number;
  /** Current page offset */
  offset: number;
  /** Whether more pages exist */
  has_more: boolean;
}

/**
 * Sponsor discovery list response.
 * Note: total field intentionally omitted - backend does not compute it.
 */
export interface SponsorProfileListResponse {
  profiles: SponsorProfile[];
  /** Current page limit */
  limit: number;
  /** Current page offset */
  offset: number;
  /** Whether more pages exist */
  has_more: boolean;
}

/**
 * Pagination options for discovery.
 */
export interface DiscoveryPagination {
  limit?: number;
  offset?: number;
}

// =============================================================================
// Errors
// =============================================================================

/**
 * Profile API error class.
 * Extends Error with safe flag for user-facing display.
 */
export class ProfileError extends Error {
  constructor(
    message: string,
    public readonly safe: boolean = true,
    public readonly statusCode?: number
  ) {
    super(message);
    this.name = 'ProfileError';
  }
}

// =============================================================================
// API Client
// =============================================================================

/**
 * Fetch helper with credentials and JSON handling.
 */
async function profileFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  const data = await response.json();

  if (!response.ok) {
    const message = data?.error || 'profile request failed';
    // 403 is "safe" - means account type doesn't have access
    // 404 is "safe" - means no profile exists yet
    // Non-404/403 errors (401, 5xx, network) are unsafe - surface to UI
    const safe = response.status === 404 || response.status === 403;
    throw new ProfileError(message, safe, response.status);
  }

  return data;
}

/**
 * Get the authenticated user's friend profile.
 * - Returns null if no profile exists (404).
 * - Throws ProfileError for 401/5xx/network errors so UI can show error state.
 */
export async function getFriendProfile(): Promise<FriendProfile | null> {
  try {
    const profile = await profileFetch<FriendProfile>('/friend/profile', {
      method: 'GET',
    });
    return profile;
  } catch (error) {
    if (error instanceof ProfileError) {
      // 404 -> no profile yet, return null
      if (error.safe) {
        return null;
      }
      // 401/5xx -> surface error to hook for UI display
      throw error;
    }
    // Network error - throw generic safe error
    throw new ProfileError('Network error', false);
  }
}

/**
 * Create or update the authenticated user's friend profile.
 * Uses POST which performs upsert (creates if not exists, updates if exists).
 * Fingerprint is optional - collection failure is non-blocking (fail-open).
 * Gender/goal/age per REQ-125, REQ-126, REQ-127.
 */
export async function createOrUpdateFriendProfile(
  data: Omit<UpdateFriendProfileRequest, 'profile_type'> & { fingerprint?: ProfileFingerprintInput }
): Promise<FriendProfile> {
  const request: UpdateFriendProfileRequest = {
    profile_type: 'friend',
    gender: data.gender,
    goal: data.goal,
    age: data.age,
    display_name: data.display_name,
    headline: data.headline,
    bio: data.bio,
    city_or_region: data.city_or_region,
    // B-2.A: the three optional work/education fields flow through
    // when present. Empty values are omitted by the caller
    // (`formToUpdatePayload`); here we forward as-is.
    work_sphere: data.work_sphere,
    education_sphere: data.education_sphere,
    education_type: data.education_type,
    ...(data.fingerprint && { fingerprint: data.fingerprint }),
  };

  const profile = await profileFetch<FriendProfile>('/friend/profile', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  return profile;
}

/**
 * Update the authenticated user's friend profile.
 * Uses PUT which performs upsert (creates if not exists, updates if exists).
 * Gender/goal/age per REQ-125, REQ-126, REQ-127.
 */
export async function updateFriendProfile(
  data: Omit<UpdateFriendProfileRequest, 'profile_type'>
): Promise<FriendProfile> {
  const request: UpdateFriendProfileRequest = {
    profile_type: 'friend',
    gender: data.gender,
    goal: data.goal,
    age: data.age,
    display_name: data.display_name,
    headline: data.headline,
    bio: data.bio,
    city_or_region: data.city_or_region,
    // B-2.A: forward the three optional work/education fields.
    work_sphere: data.work_sphere,
    education_sphere: data.education_sphere,
    education_type: data.education_type,
  };

  const profile = await profileFetch<FriendProfile>('/friend/profile', {
    method: 'PUT',
    body: JSON.stringify(request),
  });

  return profile;
}

/**
 * List friend profiles for sponsor discovery.
 * Supports pagination with limit/offset.
 * Endpoint is selected by registration kind (sponsor sees friend profiles).
 * 
 * GET /api/v1/sponsor/discovery/profiles
 */
export async function listSponsorDiscoveryProfiles(
  pagination?: DiscoveryPagination
): Promise<ProfileListResponse> {
  const params = new URLSearchParams();
  
  if (pagination?.limit !== undefined) {
    params.set('limit', String(pagination.limit));
  }
  if (pagination?.offset !== undefined) {
    params.set('offset', String(pagination.offset));
  }

  const queryString = params.toString();
  const endpoint = queryString 
    ? `/sponsor/discovery/profiles?${queryString}` 
    : '/sponsor/discovery/profiles';

  const response = await profileFetch<ProfileListResponse>(endpoint, {
    method: 'GET',
  });

  return response;
}

/**
 * List sponsor profiles for friend discovery.
 * Supports pagination with limit/offset.
 * Endpoint is selected by registration kind (friend sees sponsor profiles).
 * 
 * GET /api/v1/friend/discovery/profiles
 */
export async function listFriendDiscoveryProfiles(
  pagination?: DiscoveryPagination
): Promise<SponsorProfileListResponse> {
  const params = new URLSearchParams();
  
  if (pagination?.limit !== undefined) {
    params.set('limit', String(pagination.limit));
  }
  if (pagination?.offset !== undefined) {
    params.set('offset', String(pagination.offset));
  }

  const queryString = params.toString();
  const endpoint = queryString 
    ? `/friend/discovery/profiles?${queryString}` 
    : '/friend/discovery/profiles';

  const response = await profileFetch<SponsorProfileListResponse>(endpoint, {
    method: 'GET',
  });

  return response;
}

/**
 * List discovery profiles based on registration kind.
 * Selects the appropriate endpoint:
 * - sponsor registration -> GET /api/v1/sponsor/discovery/profiles (friend profiles)
 * - friend registration -> GET /api/v1/friend/discovery/profiles (sponsor profiles)
 * 
 * @param registrationKind - The account's registration kind
 * @param pagination - Optional pagination parameters
 */
export async function listDiscoveryProfiles(
  registrationKind: RegistrationKind,
  pagination?: DiscoveryPagination
): Promise<ProfileListResponse | SponsorProfileListResponse> {
  if (registrationKind === 'friend') {
    return listFriendDiscoveryProfiles(pagination);
  }
  // Default to sponsor view (friend profiles for sponsors)
  return listSponsorDiscoveryProfiles(pagination);
}

/**
 * Get a single friend profile by ID for sponsor card detail view.
 * 
 * GET /api/v1/sponsor/discovery/profiles/{id}
 * 
 * Returns ProfileResponse for the given profile ID.
 * Throws ProfileError for not found (404), invalid UUID, or server errors.
 * 
 * @param profileId - UUID of the profile to fetch
 * @throws ProfileError with safe=true for 404 (profile not found)
 * @throws ProfileError with safe=false for 401/5xx (server error)
 */
export async function getFriendProfileById(profileId: string): Promise<FriendProfile> {
  // Validate UUID format before making request
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(profileId)) {
    throw new ProfileError('Invalid profile ID', true);
  }

  const profile = await profileFetch<FriendProfile>(`/sponsor/discovery/profiles/${profileId}`, {
    method: 'GET',
  });

  return profile;
}

/**
 * Get a single sponsor profile by ID for friend card detail view.
 * 
 * GET /api/v1/friend/discovery/profiles/{id}
 * 
 * Returns SponsorProfile for the given profile ID.
 * Throws ProfileError for not found (404), invalid UUID, or server errors.
 * 
 * @param profileId - UUID of the profile to fetch
 * @throws ProfileError with safe=true for 404 (profile not found)
 * @throws ProfileError with safe=false for 401/5xx (server error)
 */
export async function getSponsorProfileById(profileId: string): Promise<SponsorProfile> {
  // Validate UUID format before making request
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(profileId)) {
    throw new ProfileError('Invalid profile ID', true);
  }

  const profile = await profileFetch<SponsorProfile>(`/friend/discovery/profiles/${profileId}`, {
    method: 'GET',
  });

  return profile;
}

/**
 * Get the authenticated user's sponsor profile.
 * - Returns null if no profile exists (404).
 * - Throws ProfileError for 401/403/5xx/network errors so UI can show error state.
 * - SECURITY: Only sponsor accounts may access their own sponsor profile.
 */
export async function getSponsorProfile(): Promise<SponsorProfile | null> {
  try {
    const profile = await profileFetch<SponsorProfile>('/sponsor/profile', {
      method: 'GET',
    });
    return profile;
  } catch (error) {
    if (error instanceof ProfileError) {
      // 404 -> no profile yet, return null
      if (error.safe) {
        return null;
      }
      // 401/403/5xx -> surface error to hook for UI display
      throw error;
    }
    // Network error - throw generic safe error
    throw new ProfileError('Network error', false);
  }
}

/**
 * Create or update the authenticated user's sponsor profile.
 * Uses POST which performs upsert (creates if not exists, updates if exists).
 * SECURITY: Only sponsor accounts may create/update sponsor profiles.
 * Fingerprint is optional - collection failure is non-blocking (fail-open).
 * Gender/goal/age per REQ-125, REQ-126, REQ-127.
 */
export async function createOrUpdateSponsorProfile(
  data: Omit<UpdateSponsorProfileRequest, 'profile_type'> & { fingerprint?: ProfileFingerprintInput }
): Promise<SponsorProfile> {
  const request: UpdateSponsorProfileRequest = {
    profile_type: 'sponsor',
    gender: data.gender,
    goal: data.goal,
    age: data.age,
    display_name: data.display_name,
    headline: data.headline,
    bio: data.bio,
    city_or_region: data.city_or_region,
    // B-2.A: forward the three optional work/education fields.
    work_sphere: data.work_sphere,
    education_sphere: data.education_sphere,
    education_type: data.education_type,
    ...(data.fingerprint && { fingerprint: data.fingerprint }),
  };

  const profile = await profileFetch<SponsorProfile>('/sponsor/profile', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  return profile;
}

/**
 * Update the authenticated user's sponsor profile.
 * Uses PUT which performs upsert (creates if not exists, updates if exists).
 * SECURITY: Only sponsor accounts may update sponsor profiles.
 * Fingerprint is optional - collection failure is non-blocking (fail-open).
 * Gender/goal/age per REQ-125, REQ-126, REQ-127.
 */
export async function updateSponsorProfile(
  data: Omit<UpdateSponsorProfileRequest, 'profile_type'> & { fingerprint?: ProfileFingerprintInput }
): Promise<SponsorProfile> {
  const request: UpdateSponsorProfileRequest = {
    profile_type: 'sponsor',
    gender: data.gender,
    goal: data.goal,
    age: data.age,
    display_name: data.display_name,
    headline: data.headline,
    bio: data.bio,
    city_or_region: data.city_or_region,
    // B-2.A: forward the three optional work/education fields.
    work_sphere: data.work_sphere,
    education_sphere: data.education_sphere,
    education_type: data.education_type,
    ...(data.fingerprint && { fingerprint: data.fingerprint }),
  };

  const profile = await profileFetch<SponsorProfile>('/sponsor/profile', {
    method: 'PUT',
    body: JSON.stringify(request),
  });

  return profile;
}

/**
 * Get a profile by ID based on registration kind.
 * Selects the appropriate endpoint:
 * - sponsor registration -> GET /api/v1/sponsor/discovery/profiles/{id} (friend profiles)
 * - friend registration -> GET /api/v1/friend/discovery/profiles/{id} (sponsor profiles)
 * 
 * @param profileId - UUID of the profile to fetch
 * @param registrationKind - The account's registration kind
 * @throws ProfileError with safe=true for 404 (profile not found) or 403 (access denied)
 * @throws ProfileError with safe=false for 401/5xx (server error)
 */
export async function getProfileById(
  profileId: string,
  registrationKind: RegistrationKind
): Promise<FriendProfile | SponsorProfile> {
  if (registrationKind === 'friend') {
    return getSponsorProfileById(profileId);
  }
  // Default to sponsor view (friend profile)
  return getFriendProfileById(profileId);
}

// =============================================================================
// Validation Helpers
// =============================================================================

/** Maximum display_name length */
export const MAX_DISPLAY_NAME = 100;

/** Maximum headline length */
export const MAX_HEADLINE = 200;

/** Maximum bio length */
export const MAX_BIO = 1000;

/** Maximum city_or_region length */
export const MAX_CITY_OR_REGION = 100;

/** Minimum age per REQ-125 */
export const MIN_AGE = 18;

/** Maximum age per REQ-125 */
export const MAX_AGE = 120;

/**
 * Truncate a string to max rune count.
 * UTF-8 safe: multi-byte characters are preserved.
 */
export function truncateField(value: string, maxLen: number): string {
  if (maxLen <= 0) return '';
  if (value.length === 0) return value;
  const runes = [...value];
  if (runes.length > maxLen) {
    return runes.slice(0, maxLen).join('');
  }
  return value;
}

/**
 * Validate display_name length.
 */
export function isValidDisplayName(name: string): boolean {
  return [...name].length <= MAX_DISPLAY_NAME;
}

/**
 * Validate headline length.
 */
export function isValidHeadline(headline: string): boolean {
  return [...headline].length <= MAX_HEADLINE;
}

/**
 * Validate bio length.
 */
export function isValidBio(bio: string): boolean {
  return [...bio].length <= MAX_BIO;
}

/**
 * Validate city_or_region length.
 */
export function isValidCityOrRegion(value: string): boolean {
  return [...value].length <= MAX_CITY_OR_REGION;
}

/**
 * Validate age per REQ-125 (must be 18-120).
 */
export function isValidAge(age: number): boolean {
  return age >= MIN_AGE && age <= MAX_AGE;
}

/**
 * Check if profile needs activation (has missing fields).
 */
export function needsActivation(profile: FriendProfile | SponsorProfile): boolean {
  return profile.profile_status === 'inactive' || 
         (profile.missing_activation_fields && profile.missing_activation_fields.length > 0);
}
